// Contrato único de estados del Inbox. Antes de esto el mapa de estados vivía
// copiado en cinco lugares (los dos switch de TicketInboxList, el switch del
// empty state de TicketTableContent, y dos strings armados a mano en dataSlice),
// así que cambiar qué entra en una vista pedía editar el mismo literal en todos
// y que no se escapara ninguno. Cuando se escapaba, la lista y su empty state
// respondían distinto para la misma vista.
//
// IDs verificados contra `ticket_status` en producción y contra
// storehub-api/app/Models/Tenant/TicketStatus.php (2026-08-20).

export const TICKET_STATUS = {
	NEW: 1,
	IN_PROGRESS: 2,
	CANCELLED: 3,
	CLOSED: 4,
	DELETED: 5,
	SYSTEM_CLOSED: 6,
	REOPEN: 7,
	REASSIGNED: 8,
}

export const STATUS_GROUP = {
	OPEN: 'open',
	RESOLVED: 'resolved',
	CANCELLED: 'cancelled',
}

// `5` (Borrado) no pertenece a ningún grupo: no se ofrece ni se pide nunca.
// En los cinco tenants del Inbox nuevo hay cero tickets en ese estado, pero la
// exclusión es por contrato, no porque hoy esté vacío.
export const STATUS_GROUP_IDS = {
	[STATUS_GROUP.OPEN]: [
		TICKET_STATUS.NEW,
		TICKET_STATUS.IN_PROGRESS,
		TICKET_STATUS.REOPEN,
		TICKET_STATUS.REASSIGNED,
	],
	[STATUS_GROUP.RESOLVED]: [TICKET_STATUS.CLOSED, TICKET_STATUS.SYSTEM_CLOSED],
	[STATUS_GROUP.CANCELLED]: [TICKET_STATUS.CANCELLED],
}

// Lo que se ofrece en el menú del filtro, en orden del ciclo de vida de una tarea.
// Son los estados del catálogo y no los grupos: un grupo obliga a elegir un color
// para representar a varios estados que no lo comparten, y ahí el punto miente
// ("Abiertas" en el celeste de Nuevo mientras la lista muestra tareas En proceso).
//
// Los grupos siguen existiendo en el contrato (`STATUS_GROUP_IDS`,
// `DEFAULT_STATUS_BY_VIEW`) porque son el valor inicial de las vistas; lo que se
// sacó es ofrecerlos en el menú.
//
// Dos IDs del catálogo quedan fuera:
// - 5 (Borrado): no se ofrece nunca, por contrato.
// - 8 (Reasignado): cero tickets en los cinco tenants del Inbox nuevo (medido en
//   prod el 2026-08-21) y sin entrada en `inventoryStatusColor`, así que no tiene
//   ni color ni nombre que mostrar. Sigue entrando por el grupo `open`, o sea que
//   un ticket reasignado no desaparece de la lista.
export const STATUS_MENU = [
	TICKET_STATUS.NEW,
	TICKET_STATUS.IN_PROGRESS,
	TICKET_STATUS.REOPEN,
	TICKET_STATUS.CLOSED,
	TICKET_STATUS.SYSTEM_CLOSED,
	TICKET_STATUS.CANCELLED,
]

// Nombre de los grupos. Ya no se ofrecen en el menú, pero siguen siendo el valor
// inicial de algunas vistas y pueden venir guardados en el store de una sesión
// anterior, así que el control tiene que saber cómo llamarlos.
export const STATUS_GROUP_LABEL = {
	[STATUS_GROUP.OPEN]: { labelKey: 'status_open', defaultLabel: 'Abiertas' },
	[STATUS_GROUP.RESOLVED]: { labelKey: 'status_resolved', defaultLabel: 'Resueltas' },
	[STATUS_GROUP.CANCELLED]: { labelKey: 'status_cancelled', defaultLabel: 'Canceladas' },
}

// Estado que le pone el color a cada grupo en el filtro. El color no se define acá:
// se lee de `inventoryStatusColor` con este ID, así que el punto del filtro sale en
// la misma paleta que los chips de la lista y sigue el toggle de vista del tenant
// sin recargar. Definir colores propios acá abriría un segundo sistema que se
// desincroniza en cuanto alguien retoque la paleta.
//
// Uno por grupo y no la mezcla de sus IDs: `open` junta Nuevo (sky), En proceso
// (amber) y Reabierto (orange), que no comparten color. El elegido es el estado
// cabecera de cada grupo, o sea por dónde se entra a ese grupo.
export const STATUS_GROUP_DOT_ID = {
	[STATUS_GROUP.OPEN]: TICKET_STATUS.NEW,
	[STATUS_GROUP.RESOLVED]: TICKET_STATUS.CLOSED,
	[STATUS_GROUP.CANCELLED]: TICKET_STATUS.CANCELLED,
}

// Valor inicial POR VISTA. Cada uno reproduce lo que esa vista ya hacía, salvo
// `owner`, que es el cambio pedido: "Creado por mí" ahora trae también las
// resueltas propias. Volumen medido por persona real (excluyendo las cuentas bot
// que generan el grueso de los tickets): peor caso 102 tickets, 4 páginas.
//
// `operator` arranca con los tres estados abiertos enumerados y no con el grupo
// `open`: el menú ofrece estados sueltos, así que un valor de grupo se vería en el
// control con un nombre que no está en la lista y no habría forma de volver a él.
// Enumerarlos deja los tres tildes marcados y el usuario puede sacar los que
// quiera. No incluye el 8 (Reasignado), que no se ofrece: cero tickets en los
// cinco tenants (medido en prod el 2026-08-21) y sin color en la paleta.
//
// `null` = la vista no tiene control de estado. "Menciones" y "Favorito" ignoran
// el estado a propósito: que el hallazgo se haya cerrado no borra el hecho de que
// alguien me nombró ahí, ni que yo lo haya marcado como favorito.
//
// `[]` = sin filtro de estado, o sea "Todos". No es una lista cerrada de IDs a
// propósito: si mañana aparece un estado nuevo, "Todo" lo sigue trayendo. La
// contracara es que tampoco excluye el 5 (Borrado): sin `ticket_status_id` el
// backend devuelve el catálogo entero. Hoy son cero tickets en los cinco tenants
// del piloto, y es lo que la vista "Todas" ya hacía desde antes.
export const DEFAULT_STATUS_BY_VIEW = {
	operator: [TICKET_STATUS.NEW, TICKET_STATUS.IN_PROGRESS, TICKET_STATUS.REOPEN],
	owner: [],
	stores: [],
	all: [],
	mentioned: null,
	favorite: null,
}

export const viewSupportsStatusFilter = (viewName) =>
	Array.isArray(DEFAULT_STATUS_BY_VIEW[viewName])

export const getDefaultStatusForView = (viewName) => {
	const value = DEFAULT_STATUS_BY_VIEW[viewName]
	return Array.isArray(value) ? [...value] : null
}

// Devuelve los IDs a mandar en `filter[ticket_status_id]`, o `null` cuando no hay
// que filtrar. `null` (y no la lista completa de IDs) es lo que hace que "Todos"
// signifique de verdad todos.
//
// Acepta grupos (string) y estados sueltos (número) en la misma lista: el menú
// ofrece los dos niveles y guarda cualquiera de ellos con la misma forma.
export const resolveStatusIds = (groups) => {
	if (!Array.isArray(groups) || groups.length === 0) {
		return null
	}

	const ids = groups.reduce((acc, value) => {
		if (typeof value === 'number') {
			return acc.concat(value)
		}
		return acc.concat(STATUS_GROUP_IDS[value] || [])
	}, [])
	return ids.length > 0 ? [...new Set(ids)] : null
}

